import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useId, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { Select } from '@/components/ui/Select';
import { listTenants } from '@/features/tenants/api/tenants.graphql';
import { formatDateTime } from '@/features/tenants/module-labels';
import { downloadText } from '@/lib/csv';
import { platformRoleLabel } from '@/lib/permissions';


import styles from './AuditLogViewer.module.scss';
import {
  AUDIT_EXPORT_MAX_ROWS,
  AUDIT_OPTIONS_QUERY_KEY,
  auditLogsQueryKey,
  auditLogsToCsv,
  collectAuditLogs,
  getAuditFilterOptions,
  getAuditLogs,
  type AuditLogFilter,
  type AuditScope,
} from '../api/audit.graphql';

export const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  auth: 'Acceso a la consola',
  admins: 'Administradores',
  tenants: 'Datos de tenants',
  lifecycle: 'Ciclo de vida',
  modules: 'Módulos',
  billing: 'Facturación',
  access: 'Bloqueos del sistema',
  other: 'Otras',
};

const TARGET_TYPE_LABELS: Readonly<Record<string, string>> = {
  Tenant: 'Tenant',
  TenantBranding: 'Marca del tenant',
  PlatformAdmin: 'Administrador',
  ModulePrice: 'Precio de módulo',
};

const SCOPE_NOTES: Readonly<Record<AuditScope, string>> = {
  all: 'Ves toda la auditoría de la plataforma.',
  tenant: 'Ves todo lo del tenant que elijas; sin elegir tenant, solo tus propias acciones. Sin facturación.',
  own: 'Solo ves tus propias acciones.',
};

/** Día `YYYY-MM-DD` en hora Colombia → instante UTC de su medianoche (UTC−5). */
export function bogotaDayStart(day: string, addDays = 0): string {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (date ?? 1) + addDays, 5)).toISOString();
}

interface FilterState {
  tenantId: string;
  adminId: string;
  category: string;
  action: string;
  targetType: string;
  result: string;
  fromDay: string;
  toDay: string;
}

const EMPTY: FilterState = {
  tenantId: '',
  adminId: '',
  category: '',
  action: '',
  targetType: '',
  result: '',
  fromDay: '',
  toDay: '',
};

export function toAuditFilter(state: FilterState, fixedTenantId?: string): AuditLogFilter {
  const tenantId = fixedTenantId ?? state.tenantId;
  return {
    ...(tenantId && { tenantId }),
    ...(state.adminId && { adminId: state.adminId }),
    ...(state.category && { category: state.category }),
    ...(state.action && { actions: [state.action] }),
    ...(state.targetType && { targetType: state.targetType }),
    ...(state.result && { result: state.result as 'success' | 'failure' }),
    ...(state.fromDay && { from: bogotaDayStart(state.fromDay) }),
    // "Hasta" incluye el día elegido completo.
    ...(state.toDay && { to: bogotaDayStart(state.toDay, 1) }),
  };
}

function prettyDetail(detail: string): string {
  try {
    return JSON.stringify(JSON.parse(detail), null, 2);
  } catch {
    return detail;
  }
}

interface Props {
  /** Ficha del tenant: el filtro de tenant queda fijo y oculto. */
  fixedTenantId?: string;
}

/**
 * OS-1726 — visor de `PlatformAuditLog`: filtros combinados, "Cargar más" por cursor y exportación
 * CSV del mismo filtro. Qué filas ve cada rol y qué datos se ocultan lo decide el API.
 */
export function AuditLogViewer({ fixedTenantId }: Props): JSX.Element {
  const idPrefix = useId();
  const [state, setState] = useState<FilterState>(EMPTY);
  const [exportNote, setExportNote] = useState('');
  const filter = useMemo(() => toAuditFilter(state, fixedTenantId), [state, fixedTenantId]);
  const invalidRange = Boolean(state.fromDay && state.toDay && state.fromDay > state.toDay);

  const options = useQuery({ queryKey: AUDIT_OPTIONS_QUERY_KEY, queryFn: getAuditFilterOptions });
  const tenants = useQuery({
    queryKey: ['platform-tenants', 'audit-filter'],
    queryFn: () => listTenants({ take: 100 }),
    enabled: !fixedTenantId,
  });
  const logs = useInfiniteQuery({
    queryKey: auditLogsQueryKey(filter),
    queryFn: ({ pageParam }) => getAuditLogs(filter, { cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    enabled: !invalidRange,
  });
  const exporter = useMutation({
    mutationFn: () => collectAuditLogs(filter),
    onSuccess: ({ entries, truncated }) => {
      const date = new Date().toISOString().slice(0, 10);
      downloadText(`auditoria-${date}.csv`, auditLogsToCsv(entries));
      setExportNote(
        truncated
          ? `Se exportaron las primeras ${AUDIT_EXPORT_MAX_ROWS.toLocaleString('es-CO')} filas; acota el filtro para el resto.`
          : `Se exportaron ${entries.length} filas con el filtro actual.`,
      );
    },
  });

  const entries = logs.data?.pages.flatMap((page) => page.entries) ?? [];
  const scope = logs.data?.pages[0]?.scope ?? options.data?.scope;
  const actions = (options.data?.actions ?? []).filter(
    (option) => !state.category || option.category === state.category,
  );
  const set = (key: keyof FilterState, value: string): void => {
    setExportNote('');
    setState((current) => ({
      ...current,
      [key]: value,
      // Cambiar de categoría limpia una acción que ya no pertenece a ella.
      ...(key === 'category' && { action: '' }),
    }));
  };
  const field = (key: keyof FilterState): string => `${idPrefix}-${key}`;

  return (
    <section className={styles.viewer} aria-label="Auditoría">
      {scope ? <p className={styles.muted}>{SCOPE_NOTES[scope]}</p> : null}

      <fieldset className={styles.filters}>
        <legend className={styles.legend}>Filtros</legend>
        {!fixedTenantId ? (
          <label className={styles.field} htmlFor={field('tenantId')}>
            Tenant
            <Select
              id={field('tenantId')}
              value={state.tenantId}
              onChange={(value) => set('tenantId', value)}
              options={[
                { value: '', label: 'Todos' },
                ...(tenants.data?.edges ?? []).map((tenant) => ({ value: tenant.id, label: tenant.name })),
              ]}
            />
          </label>
        ) : null}
        {options.data && options.data.admins.length > 0 ? (
          <label className={styles.field} htmlFor={field('adminId')}>
            Administrador
            <Select
              id={field('adminId')}
              value={state.adminId}
              onChange={(value) => set('adminId', value)}
              options={[
                { value: '', label: 'Todos' },
                ...options.data.admins.map((admin) => ({
                  value: admin.id,
                  label: `${admin.email} (${platformRoleLabel(admin.platformRole)})`,
                })),
              ]}
            />
          </label>
        ) : null}
        <label className={styles.field} htmlFor={field('category')}>
          Categoría
          <Select
            id={field('category')}
            value={state.category}
            onChange={(value) => set('category', value)}
            options={[
              { value: '', label: 'Todas' },
              ...(options.data?.categories ?? []).map((category) => ({
                value: category,
                label: CATEGORY_LABELS[category] ?? category,
              })),
            ]}
          />
        </label>
        <label className={styles.field} htmlFor={field('action')}>
          Acción
          <Select
            id={field('action')}
            value={state.action}
            onChange={(value) => set('action', value)}
            options={[
              { value: '', label: 'Todas' },
              ...actions.map((option) => ({ value: option.action, label: option.label })),
            ]}
          />
        </label>
        <label className={styles.field} htmlFor={field('targetType')}>
          Entidad
          <Select
            id={field('targetType')}
            value={state.targetType}
            onChange={(value) => set('targetType', value)}
            options={[
              { value: '', label: 'Todas' },
              ...(options.data?.targetTypes ?? []).map((type) => ({
                value: type,
                label: TARGET_TYPE_LABELS[type] ?? type,
              })),
            ]}
          />
        </label>
        <label className={styles.field} htmlFor={field('result')}>
          Resultado
          <Select
            id={field('result')}
            value={state.result}
            onChange={(value) => set('result', value)}
            options={[
              { value: '', label: 'Todos' },
              { value: 'success', label: 'Exitoso' },
              { value: 'failure', label: 'Fallido o bloqueado' },
            ]}
          />
        </label>
        <label className={styles.field} htmlFor={field('fromDay')}>
          Desde
          <input id={field('fromDay')} type="date" value={state.fromDay} onChange={(e) => set('fromDay', e.target.value)} />
        </label>
        <label className={styles.field} htmlFor={field('toDay')}>
          Hasta
          <input id={field('toDay')} type="date" value={state.toDay} onChange={(e) => set('toDay', e.target.value)} />
        </label>
        <div className={styles.filterActions}>
          <button type="button" className={styles.linkBtn} onClick={() => setState(EMPTY)}>
            Limpiar filtros
          </button>
          <button
            type="button"
            className={styles.exportBtn}
            onClick={() => exporter.mutate()}
            disabled={exporter.isPending || invalidRange || entries.length === 0}
          >
            <Download size={14} aria-hidden="true" />
            {exporter.isPending ? 'Exportando…' : 'Exportar CSV'}
          </button>
        </div>
      </fieldset>

      <p className={styles.notice} role="status" aria-live="polite">
        {exporter.isError ? `No se pudo exportar: ${exporter.error.message}` : exportNote}
      </p>

      {invalidRange ? (
        <p className={styles.error} role="alert">
          La fecha "Desde" debe ser anterior o igual a "Hasta".
        </p>
      ) : logs.isLoading ? (
        <p className={styles.muted}>Cargando auditoría…</p>
      ) : logs.isError ? (
        <div className={styles.error} role="alert">
          No se pudo cargar la auditoría: {logs.error.message}{' '}
          <button type="button" className={styles.linkBtn} onClick={() => void logs.refetch()}>
            Reintentar
          </button>
        </div>
      ) : entries.length === 0 ? (
        <p className={styles.muted}>No hay registros con estos filtros.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className={styles.srOnly}>Registros de auditoría</caption>
            <thead>
              <tr>
                <th scope="col">Fecha</th>
                <th scope="col">Acción</th>
                <th scope="col">Actor</th>
                <th scope="col">Objetivo</th>
                <th scope="col">IP / navegador</th>
                <th scope="col">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className={styles.nowrap}>{formatDateTime(entry.createdAt)}</td>
                  <th scope="row" className={styles.action}>
                    {entry.actionLabel}
                    {entry.outcome === 'failure' ? <span className={styles.failure}>fallido</span> : null}
                    <span className={styles.code}>{entry.action}</span>
                  </th>
                  <td>
                    {entry.actorEmail ?? (entry.actorId ? entry.actorId : 'Sistema')}
                    {entry.actorRole ? (
                      <span className={styles.code}>{platformRoleLabel(entry.actorRole)}</span>
                    ) : null}
                  </td>
                  <td>
                    {entry.tenantId && !fixedTenantId ? (
                      <Link to={`/tenants/${entry.tenantId}`}>{entry.targetLabel ?? entry.tenantId}</Link>
                    ) : (
                      (entry.targetLabel ?? entry.targetId ?? '—')
                    )}
                    {entry.targetType ? (
                      <span className={styles.code}>{TARGET_TYPE_LABELS[entry.targetType] ?? entry.targetType}</span>
                    ) : null}
                  </td>
                  <td className={styles.network}>
                    {entry.ipAddress || entry.userAgent ? (
                      <>
                        {entry.ipAddress ?? '—'}
                        {entry.userAgent ? <span className={styles.code}>{entry.userAgent}</span> : null}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {entry.detail ? (
                      <details>
                        <summary>Ver</summary>
                        <pre className={styles.detail}>{prettyDetail(entry.detail)}</pre>
                      </details>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logs.hasNextPage && !invalidRange ? (
        <button
          type="button"
          className={styles.secondaryBtn}
          disabled={logs.isFetchingNextPage}
          onClick={() => void logs.fetchNextPage()}
        >
          {logs.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
        </button>
      ) : null}
    </section>
  );
}
